'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { logWhenReady } from '@/app/analytics';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { useDropUnknownCarrier } from '../hooks/useDropUnknownCarrier';
import { useSignupSponsor } from '../hooks/useSignupSponsor';
import { endLeftoverSession } from '../services/leftover-session';
import { linkNamesSponsor } from '../services/sponsor-lookup';
import { useSignupFlowStore } from '../stores/signup-flow-store';
import { SIGNUP_LOGIN_PAGE } from '../off-path-pages';
import {
  personifyAuthorizeUrl,
  personifyGateDecision,
  readPersonifyConfig,
} from '../services/personify-gate';
import { PersonifyLoginNotice } from './PersonifyLoginNotice';
import { EXPRESS_SCRIPTS_CLIENT_ID, FIRST_SIGNUP_STEP, firstSignupStep } from '../steps';
import { isDirectDownload } from '../download/direct-download';
import { parseLegacySignupParams } from '../services/legacy-params';
import {
  legalDocumentFromParams,
  legalDocumentPath,
  type LegalDocumentId,
} from '../legal/legal-document-paths';

/**
 * A returning member's link (`?login`, production's entry): any value but
 * `false` counts, as on the legacy signup app.
 */
export function isLoginEntry(params: URLSearchParams): boolean {
  return params.has('login') && params.get('login') !== 'false';
}

/**
 * Personify's login hand-off for this entry: where to send the member, or null
 * when the gate does not redirect (another client, a return from Personify, or
 * the gate switched off). Read from the query, so it is known on first render.
 */
function usePersonifyGate(query: string) {
  return useMemo(() => {
    const params = new URLSearchParams(query);
    const config = readPersonifyConfig();
    const decision = personifyGateDecision(params, config);
    const authorizeUrl =
      decision.applies && decision.reason === 'redirect' && config && typeof window !== 'undefined'
        ? personifyAuthorizeUrl(config, window.location.origin, params)
        : null;
    return { decision, authorizeUrl };
  }, [query]);
}

/**
 * Starts the sponsor lookup the following steps read, in the same query they
 * use, so the answer is already on its way when they render. A fresh API copy
 * can take seconds to answer its first lookup.
 */
function SponsorPrefetch({ clientId, subClientId }: { clientId: string; subClientId?: string }) {
  useSignupSponsor(clientId, subClientId);
  return null;
}

function SignupEntry({ login }: { login: boolean }) {
  const { clientId, subClientId, directToReg, download } = useLegacySignupParams();
  const directDownload = isDirectDownload(download);
  const { goToStep, goToNextStep, path } = useSignupNavigation();
  const resetFlow = useSignupFlowStore((state) => state.resetFlow);
  const query = useSearchParams().toString();
  const { decision, authorizeUrl } = usePersonifyGate(query);
  // Personify's gate applies only where the legacy app ran it: the employer
  // link's new-member flow, after the download, login and no-employer branches.
  const personifyEntry = !directDownload && !login && path !== 'no-employer';
  const personifyRedirect = personifyEntry && authorizeUrl !== null;
  const personifyLogged = useRef(false);
  useEffect(() => {
    if (!personifyEntry || !decision.applies || personifyLogged.current) return;
    // Once per entry, for Personify's own links, with nothing from the URL.
    personifyLogged.current = true;
    void logWhenReady('info', 'galapagos.signup.personify_gate_decided', {
      redirect: decision.reason === 'redirect',
      reason: decision.reason,
    });
  }, [personifyEntry, decision]);
  useEffect(() => {
    // A sign-in an earlier member left in this tab is not this link's.
    endLeftoverSession();
    // The entry link starts a new flow, so earlier choices are forgotten.
    resetFlow();
    // The direct-download link wins over every other entry param, login
    // included, as on the legacy signup: the download page shows its badges
    // and, on a phone, then opens that phone's store.
    if (directDownload) goToStep('download');
    // Login comes next, as production checks it before the rest.
    else if (login) goToStep(SIGNUP_LOGIN_PAGE);
    // A link with no employer starts with the member's own details, and an
    // employer SSO member with the choice of where their code goes.
    else if (path !== 'employer-link') goToStep(firstSignupStep(path));
    // Personify Health's members sign in to Personify's portal first; the
    // notice below sends them there, and a return from there carries on.
    else if (personifyRedirect) return;
    // A direct-to-registration link skips the context screen.
    else if (directToReg) goToNextStep(FIRST_SIGNUP_STEP);
    else goToStep(FIRST_SIGNUP_STEP);
  }, [
    directDownload,
    login,
    directToReg,
    goToStep,
    goToNextStep,
    path,
    personifyRedirect,
    resetFlow,
  ]);
  // Only for an entry that goes on into the flow with the link's sponsor. A
  // carrier parent link (no-employer path) names a parent, not the sponsor:
  // the member picks the carrier later, as useActiveSponsorQuery reads it.
  const prefetchSponsor =
    path !== 'no-employer' &&
    linkNamesSponsor(clientId) &&
    !directDownload &&
    !login &&
    !personifyRedirect;
  return (
    <>
      {prefetchSponsor && clientId !== undefined ? (
        <SponsorPrefetch clientId={clientId} subClientId={subClientId} />
      ) : null}
      {personifyRedirect && authorizeUrl ? (
        <PersonifyLoginNotice authorizeUrl={authorizeUrl} />
      ) : null}
    </>
  );
}

/**
 * A legal-document link (`?privacy_policy#cookie-notice`) opens that page
 * instead of the flow, as on the legacy signup app. The hash rides along; the
 * other params do not, since nothing on a legal page reads them.
 */
function LegalEntryRedirect({ documentId }: { documentId: LegalDocumentId }) {
  const router = useRouter();
  useEffect(() => {
    router.replace(`${legalDocumentPath(documentId)}${window.location.hash}`);
  }, [router, documentId]);
  return null;
}

/**
 * Holds the entry of an Express Scripts link that names a carrier until the
 * carrier is looked up. A carrier the portal does not know starts over as a
 * plain Express Scripts link, so the member searches for their carrier rather
 * than stopping, as legacy signup carried on as plain Express Scripts. Any
 * other answer lets the entry go on as usual, sharing the same lookup.
 */
function CarrierLookupGate({ clientId, subClientId }: { clientId: string; subClientId: string }) {
  const sponsor = useSignupSponsor(clientId, subClientId);
  const dropping = useDropUnknownCarrier(sponsor, 'entry');
  return sponsor.isPending || dropping ? null : <SignupEntry login={false} />;
}

function SignupEntryRoute() {
  const params = useSearchParams();
  const legalDocument = legalDocumentFromParams(params);
  if (legalDocument) return <LegalEntryRedirect documentId={legalDocument} />;
  const login = isLoginEntry(params);
  const { clientId, subClientId, download } = parseLegacySignupParams(params.toString());
  if (
    !login &&
    !isDirectDownload(download) &&
    clientId === EXPRESS_SCRIPTS_CLIENT_ID &&
    subClientId !== undefined
  ) {
    return <CarrierLookupGate clientId={clientId} subClientId={subClientId} />;
  }
  return <SignupEntry login={login} />;
}

/**
 * `/`: reads the legacy params, then replaces itself with the first
 * step, so the entry never becomes a history entry of its own.
 */
export function SignupEntryScreen() {
  return <SignupEntryRoute />;
}
